import { useRef, useState } from 'react';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDeleteAgentConversation } from '@/hooks/mutations/agents/use-delete-agent-conversation';
import { useRenameAgentConversation } from '@/hooks/mutations/agents/use-rename-agent-conversation';
import {
  promptQueueKey,
  useChatPromptQueueStore,
} from '@/stores/chat-prompt-queue-store';
import { useOrgStore } from '@/stores/org-store';

interface RenameConversationModalProps {
  agentId: string;
  conversationId: string;
  currentTitle: string;
  /** Seeds the input when a failed rename brings the dialog back. */
  initialTitle?: string;
  onClose: () => void;
  onSubmitted: (title: string) => void;
  onFailed?: (title: string) => void;
}

// Mounted only while open so the draft starts from the current title without
// a sync effect, and a background list refetch can't overwrite what is typed.
export function RenameConversationModal({
  agentId,
  conversationId,
  currentTitle,
  initialTitle,
  onClose,
  onSubmitted,
  onFailed,
}: RenameConversationModalProps) {
  const { mutateAsync: renameConversation } =
    useRenameAgentConversation(agentId);
  const [draftTitle, setDraftTitle] = useState(initialTitle ?? currentTitle);

  const handleRename = async () => {
    const trimmed = draftTitle.trim();
    // Equality first: an untitled chat arrives with an empty currentTitle, and
    // submitting it untouched should close like any other unchanged name
    // rather than fall into the empty-name guard and sit there.
    if (trimmed === currentTitle) {
      onClose();
      return;
    }
    if (!trimmed) return;
    // Closed on submit rather than on success: the list already carries the
    // new title optimistically, so holding the dialog up only hides it.
    onSubmitted(trimmed);
    try {
      await renameConversation({ conversationId, title: trimmed });
    } catch {
      onFailed?.(trimmed);
    }
  };

  return (
    <Modal
      title='Rename chat'
      description='Give this chat a name you will recognize later.'
      isDialogOpen
      setIsDialogOpen={(open) => {
        if (!open) onClose();
      }}
      footer={
        <>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleRename()}>Rename</Button>
        </>
      }
    >
      <Input
        value={draftTitle}
        onChange={(e) => setDraftTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void handleRename()}
        placeholder='Chat name'
        autoFocus
      />
    </Modal>
  );
}

// Owns the dialog's open state so both chat surfaces reopen a failed rename
// the same way, with the draft intact and the card already reverted.
export function useRenameConversationDialog<T>() {
  const [target, setTarget] = useState<T | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [failedTitle, setFailedTitle] = useState<string>();
  const latestSubmit = useRef<string | null>(null);

  const openRename = (next: T) => {
    setFailedTitle(undefined);
    setTarget(next);
    setIsOpen(true);
  };

  return {
    renameTarget: isOpen ? target : null,
    openRename,
    renameProps: {
      initialTitle: failedTitle,
      onClose: () => setIsOpen(false),
      onSubmitted: (title: string) => {
        latestSubmit.current = title;
        setIsOpen(false);
      },
      onFailed: (title: string) => {
        // Only the newest submit may reopen: an older failure must not put the
        // dialog back over a title the user has since replaced.
        if (latestSubmit.current !== title) return;
        setFailedTitle(title);
        setIsOpen(true);
      },
    },
  };
}

interface DeleteConversationModalProps {
  agentId: string;
  conversationId: string;
  /** What the chat is called on screen, not its stored title. */
  label: string;
  onClose: () => void;
  onDeleted?: () => void;
}

// Dismissal is blocked while the delete is in flight: this component owns
// the mutation, so unmounting mid-request drops onDeleted and strands the
// caller on a conversation that no longer exists.
export function DeleteConversationModal({
  agentId,
  conversationId,
  label,
  onClose,
  onDeleted,
}: DeleteConversationModalProps) {
  const { mutate: deleteConversation, isPending } =
    useDeleteAgentConversation(agentId);
  const orgId = useOrgStore((s) => s.currentOrg?.orgid);
  const clearPromptQueue = useChatPromptQueueStore((s) => s.clear);

  const handleDelete = () => {
    deleteConversation(conversationId, {
      onSuccess: () => {
        // Queued prompts are keyed to the conversation; with it gone
        // nothing can ever dispatch them, so don't leave them persisted.
        clearPromptQueue(promptQueueKey(orgId, agentId, conversationId));
        onClose();
        onDeleted?.();
      },
    });
  };

  return (
    <Modal
      title='Delete chat?'
      description={`“${label}” will no longer appear in your chats.`}
      isDialogOpen
      setIsDialogOpen={(open) => {
        if (!open) onClose();
      }}
      preventClose={isPending}
      hideCloseButton={isPending}
      footer={
        <>
          <Button variant='outline' onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            disabled={isPending}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </>
      }
    />
  );
}
